import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import SmartExChap from './components/smartexChap';
import Authentification from './components/Authentification';
import MesDevoirs from './components/MesDevoirs'; 
import { Bell, X } from 'lucide-react';
import { subscribeUserToPush } from './utils/pushNotifications';
import './App.css';

/**
 * 🎬 COMPOSANT DE TRANSITION
 */
const PageTransition = ({ children }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, filter: "blur(4px)" }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
      className="w-full min-h-screen absolute top-0 left-0"
    >
      {children}
    </motion.div>
  );
};

/**
 * 🛣️ GESTIONNAIRE DE ROUTES ANIMÉES
 */
const AnimatedRoutes = ({ isAuthenticated, user, handleLogin, handleLogout, getDefaultRoute }) => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        
        <Route 
          path="/auth" 
          element={
            <PageTransition>
              {isAuthenticated ? 
                <Navigate to={getDefaultRoute()} replace /> : 
                <Authentification onLogin={handleLogin} />
              }
            </PageTransition>
          } 
        />
        
        <Route 
          path="/smartex-chap" 
          element={
            <PageTransition>
              {/* Suppression de la condition user?.plan === 'Premium' */}
              {isAuthenticated ? 
                <SmartExChap onLogout={handleLogout} /> : 
                <Navigate to={getDefaultRoute()} replace />
              }
            </PageTransition>
          } 
        />

        <Route 
          path="/mes-devoirs" 
          element={
            <PageTransition>
              {isAuthenticated && user?.role === 'student_partner' ? 
                <MesDevoirs onLogout={handleLogout} /> : 
                <Navigate to={getDefaultRoute()} replace />
              }
            </PageTransition>
          } 
        />

        <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />
        <Route path="*" element={<Navigate to={getDefaultRoute()} replace />} />
      </Routes>
    </AnimatePresence>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null); 
  const [isLoading, setIsLoading] = useState(true);
  const [showNotificationBanner, setShowNotificationBanner] = useState(false);
  
  useEffect(() => {
    const userInfo = sessionStorage.getItem('user_info');
    
    if (userInfo) {
      try {
        const parsedUserInfo = JSON.parse(userInfo);
        
        // On vérifie juste que l'utilisateur existe bien (plus de check Premium)
        if (parsedUserInfo && parsedUserInfo.email) {
          setIsAuthenticated(true);
          setUser(parsedUserInfo);
        } else {
          sessionStorage.removeItem('user_info'); 
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        sessionStorage.removeItem('user_info'); 
        setIsAuthenticated(false);
        setUser(null);
      }
    } else {
      setIsAuthenticated(false);
      setUser(null);
    }
    
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      if ('Notification' in window && Notification.permission === 'default') {
        const timer = setTimeout(() => setShowNotificationBanner(true), 2000);
        return () => clearTimeout(timer);
      }
    } else {
      setShowNotificationBanner(false);
    }
  }, [isAuthenticated]);

  const handleLogout = () => {
    sessionStorage.removeItem('user_info'); 
    localStorage.removeItem('token'); // On nettoie aussi le token Supabase par sécurité
    setIsAuthenticated(false);
    setUser(null);
  };

  const handleLogin = () => {
    const userInfo = sessionStorage.getItem('user_info'); 
    
    if (userInfo) {
      try {
        const parsedUserInfo = JSON.parse(userInfo);
        
        // Plus de check Premium ici non plus
        if (parsedUserInfo && parsedUserInfo.email) {
          setIsAuthenticated(true);
          setUser(parsedUserInfo);
        } else {
          sessionStorage.removeItem('user_info'); 
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        sessionStorage.removeItem('user_info'); 
        setIsAuthenticated(false);
        setUser(null);
      }
    }
  };

  const getDefaultRoute = () => {
    if (!isAuthenticated || !user) return "/auth";
    return user.role === 'student_partner' ? "/mes-devoirs" : "/smartex-chap";
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f8fafc' }}>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
          Chargement...
        </motion.div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <div className="App relative min-h-screen bg-slate-50 overflow-x-hidden">
        <AnimatedRoutes 
          isAuthenticated={isAuthenticated}
          user={user}
          handleLogin={handleLogin}
          handleLogout={handleLogout}
          getDefaultRoute={getDefaultRoute}
        />
        <style>{`
          @keyframes slide-up-push {
            0% { transform: translateY(150%); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
          }
          .animate-slide-up {
            animation: slide-up-push 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }
        `}</style>
      </div>
    </BrowserRouter>
  );
}

export default App;